import { useState, useEffect } from "react";
import { Settings, GripVertical, Eye, EyeOff } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

export interface LineItemColumn {
  id: string;
  label: string;
  visible: boolean;
  required?: boolean; // Required columns cannot be hidden
}

interface ConfigureLineItemsColumnsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columns: LineItemColumn[];
  onSave: (columns: LineItemColumn[]) => void;
}

export function ConfigureLineItemsColumnsModal({
  open,
  onOpenChange,
  columns,
  onSave,
}: ConfigureLineItemsColumnsModalProps) {
  const [localColumns, setLocalColumns] = useState<LineItemColumn[]>(columns);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Update local state when columns prop changes
  useEffect(() => {
    setLocalColumns(columns);
  }, [columns]);

  const handleToggleColumn = (columnId: string) => {
    setLocalColumns((prev) =>
      prev.map((col) =>
        col.id === columnId && !col.required
          ? { ...col, visible: !col.visible }
          : col
      )
    );
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex) {
      const newColumns = [...localColumns];
      const [draggedColumn] = newColumns.splice(draggedIndex, 1);
      newColumns.splice(dragOverIndex, 0, draggedColumn);
      setLocalColumns(newColumns);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleSave = () => {
    onSave(localColumns);
    onOpenChange(false);
  };

  const handleReset = () => {
    // Reset to default order and visibility
    const defaultColumns: LineItemColumn[] = [
      { id: "itemCode", label: "Item Code", visible: true },
      { id: "description", label: "Description", visible: true, required: true },
      { id: "project", label: "Project", visible: true },
      { id: "projectName", label: "Project Name", visible: false },
      { id: "costCentre", label: "Cost Centre", visible: true },
      { id: "costCentreName", label: "Cost Centre Name", visible: false },
      { id: "quantity", label: "Qty", visible: true, required: true },
      { id: "unitPrice", label: "Unit Price", visible: true, required: true },
      { id: "subtotal", label: "Net", visible: true, required: true },
      { id: "taxAmount", label: "Tax", visible: true },
      { id: "total", label: "Gross", visible: true, required: true },
      { id: "currencyGross", label: "Currency Gross", visible: false },
      { id: "currencyNet", label: "Currency Net", visible: false },
      { id: "currencyVAT", label: "Currency VAT", visible: false },
    ];
    setLocalColumns(defaultColumns);
  };

  const visibleCount = localColumns.filter((col) => col.visible).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Configure Table Columns
          </DialogTitle>
          <DialogDescription>
            Show, hide, and reorder columns in the Line Items table. Drag to reorder.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Summary */}
          <div className="flex items-center justify-between px-4 py-2 bg-muted/50 rounded-md text-sm">
            <span className="text-muted-foreground">
              {visibleCount} of {localColumns.length} columns visible
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="h-7 text-xs"
            >
              Reset to Default
            </Button>
          </div>

          {/* Column List */}
          <ScrollArea className="h-[400px] rounded-md border">
            <ScrollAreaViewport className="h-full w-full">
              <div className="p-2 space-y-1">
                {localColumns.map((column, index) => (
                  <div
                    key={column.id}
                    draggable={!column.required}
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-md border bg-background transition-all",
                      draggedIndex === index && "opacity-50",
                      dragOverIndex === index && draggedIndex !== index && "border-primary",
                      !column.required && "cursor-move hover:bg-muted/50"
                    )}
                  >
                    {/* Drag Handle */}
                    <div
                      className={cn(
                        "flex-shrink-0",
                        column.required ? "opacity-30 cursor-not-allowed" : "cursor-grab active:cursor-grabbing"
                      )}
                    >
                      <GripVertical className="h-4 w-4 text-muted-foreground" />
                    </div>

                    {/* Checkbox */}
                    <Checkbox
                      id={`column-${column.id}`}
                      checked={column.visible}
                      onCheckedChange={() => handleToggleColumn(column.id)}
                      disabled={column.required}
                      className="flex-shrink-0"
                    />

                    {/* Label */}
                    <label
                      htmlFor={`column-${column.id}`}
                      className={cn(
                        "flex-1 text-sm font-medium cursor-pointer select-none",
                        !column.visible && "text-muted-foreground",
                        column.required && "cursor-default"
                      )}
                    >
                      {column.label}
                      {column.required && (
                        <span className="ml-2 text-xs text-muted-foreground">(Required)</span>
                      )}
                    </label>

                    {/* Visibility Icon */}
                    <div className="flex-shrink-0">
                      {column.visible ? (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>

          {/* Help Text */}
          <div className="text-xs text-muted-foreground px-1">
            <p>• Drag columns to reorder them in the table</p>
            <p>• Uncheck to hide columns (required columns cannot be hidden)</p>
            <p>• Your preferences will be saved automatically</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
